import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon, 
  Video, 
  Clock, 
  Users,
  Building2,
  Loader2,
  ExternalLink,
  MessageSquare,
  RefreshCcw,
  Send,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Paperclip,
  Download,
  Plus,
  Sun,
  Sunrise,
  Moon,
  MoreHorizontal,
  MapPin,
  User,
  ArrowRight,
  Sparkles,
  CalendarDays,
  ListTodo
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday, formatDistanceToNow, startOfWeek, endOfWeek, addDays, isBefore, isAfter } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const meetingStatuses = [
  { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-500' },
  { value: 'completed', label: 'Completed', color: 'bg-green-500' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500' }
];

const CalendarPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [clients, setClients] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  
  // Meeting detail modal state
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [comments, setComments] = useState([]);
  const [rescheduleRequests, setRescheduleRequests] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState('10:00');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;
  const isClient = user?.role === 'client' || user?.role === 'client_team';
  const isClientTeam = user?.role === 'client_team';

  const fetchData = async () => {
    setLoading(true);
    try {
      const [meetingsRes, tenantsRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/meetings`, getAuthHeaders()),
        axios.get(`${API_URL}/tenants`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/clients`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setMeetings(meetingsRes.data || []);
      setTenants(tenantsRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSyncToA2G = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_URL}/calendar/sync-now`, {}, getAuthHeaders());
      setLastSync(new Date());
      toast.success(`${res.data.count} events synced to A2G Office!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed — check A2G Office configuration');
    } finally {
      setSyncing(false);
    }
  };

  const fetchMeetingDetails = async (meetingId) => {
    try {
      const [commentsRes, requestsRes, attachmentsRes] = await Promise.all([
        axios.get(`${API_URL}/meetings/${meetingId}/comments`, getAuthHeaders()),
        axios.get(`${API_URL}/meetings/${meetingId}/reschedule-requests`, getAuthHeaders()),
        axios.get(`${API_URL}/meetings/${meetingId}/attachments`, getAuthHeaders())
      ]);
      setComments(commentsRes.data || []);
      setRescheduleRequests(requestsRes.data || []);
      setAttachments(attachmentsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch meeting details:', error);
    }
  };

  const handleOpenMeetingDetail = async (meeting) => {
    setSelectedMeeting(meeting);
    setIsDetailOpen(true);
    await fetchMeetingDetails(meeting.id);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedMeeting) return;
    setCommentLoading(true);
    try {
      await axios.post(`${API_URL}/meetings/${selectedMeeting.id}/comments`, {
        content: newComment
      }, getAuthHeaders());
      toast.success('Comment added');
      setNewComment('');
      await fetchMeetingDetails(selectedMeeting.id);
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleRequestReschedule = async () => {
    if (!rescheduleDate || !selectedMeeting) {
      toast.error('Please select a new date');
      return;
    }
    setRescheduleLoading(true);
    try {
      const requestedDate = new Date(rescheduleDate);
      const [hours, minutes] = rescheduleTime.split(':');
      requestedDate.setHours(parseInt(hours), parseInt(minutes));
      
      await axios.post(`${API_URL}/meetings/${selectedMeeting.id}/reschedule-requests`, {
        requested_date: requestedDate.toISOString(),
        reason: rescheduleReason
      }, getAuthHeaders());
      
      toast.success('Reschedule request submitted');
      setIsRescheduleOpen(false);
      setRescheduleDate(null);
      setRescheduleTime('10:00');
      setRescheduleReason('');
      await fetchMeetingDetails(selectedMeeting.id);
    } catch (error) {
      toast.error('Failed to submit reschedule request');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleRespondToReschedule = async (requestId, action) => {
    try {
      await axios.put(
        `${API_URL}/meetings/${selectedMeeting.id}/reschedule-requests/${requestId}`,
        { action },
        getAuthHeaders()
      );
      toast.success(`Request ${action}d`);
      await fetchMeetingDetails(selectedMeeting.id);
      fetchData();
    } catch (error) {
      toast.error('Failed to respond to request');
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;
    try {
      await axios.delete(`${API_URL}/meetings/${meetingId}`, getAuthHeaders());
      toast.success('Meeting deleted');
      setIsDetailOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete meeting');
    }
  };

  const handleUpdateMeetingStatus = async (status) => {
    if (!selectedMeeting) return;
    try {
      await axios.put(`${API_URL}/meetings/${selectedMeeting.id}`, { status }, getAuthHeaders());
      toast.success('Meeting status updated');
      setSelectedMeeting({ ...selectedMeeting, status });
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getTenantName = (orgId) => {
    const tenant = tenants.find(t => t.id === orgId);
    return tenant?.company_name || tenant?.name || 'Unknown';
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company || client?.name || '';
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes('pdf')) return '📄';
    if (contentType?.includes('word') || contentType?.includes('document')) return '📝';
    if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) return '📊';
    if (contentType?.includes('zip')) return '📦';
    if (contentType?.includes('image')) return '🖼️';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await axios.get(
        `${API_URL}/meetings/${selectedMeeting.id}/attachments/${attachment.id}/download`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  // Calendar calculations
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getMeetingsForDate = (date) => {
    if (!date) return [];
    return meetings.filter(meeting => {
      const meetingDate = new Date(meeting.scheduled_at);
      return isSameDay(meetingDate, date);
    });
  };

  const getUpcomingMeetings = () => {
    const now = new Date();
    return meetings
      .filter(m => new Date(m.scheduled_at) >= now && m.status === 'scheduled')
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
      .slice(0, 5);
  };

  const getTodayMeetings = () => {
    return getMeetingsForDate(new Date());
  };

  const selectedDateMeetings = selectedDate ? getMeetingsForDate(selectedDate) : [];

  const navigateMonth = (direction) => {
    setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-500';
      case 'completed': return 'bg-emerald-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-slate-500';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Scheduled</Badge>;
      case 'completed': return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Completed</Badge>;
      case 'cancelled': return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Cancelled</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTimeOfDayIcon = (date) => {
    const hour = new Date(date).getHours();
    if (hour < 12) return <Sunrise size={14} className="text-amber-500" />;
    if (hour < 17) return <Sun size={14} className="text-yellow-500" />;
    return <Moon size={14} className="text-indigo-500" />;
  };

  const totalMeetings = meetings.length;
  const scheduledCount = meetings.filter(m => m.status === 'scheduled').length;
  const completedCount = meetings.filter(m => m.status === 'completed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-slate-200 border-t-indigo-500 rounded-full animate-spin mx-auto"></div>
            <CalendarIcon className="absolute inset-0 m-auto w-6 h-6 text-indigo-500" />
          </div>
          <p className="mt-4 text-slate-600 font-medium">Loading your calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.05%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30"></div>
        <div className="relative px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                    <CalendarDays size={24} />
                  </div>
                  <h1 className="text-3xl font-bold tracking-tight">My Calendar</h1>
                </div>
                <p className="text-indigo-100 text-lg">
                  {format(new Date(), 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
              
              {/* Quick Stats + Sync Button */}
              <div className="flex items-center gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3 border border-white/20">
                  <p className="text-indigo-200 text-xs uppercase tracking-wide">Total</p>
                  <p className="text-2xl font-bold">{totalMeetings}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3 border border-white/20">
                  <p className="text-indigo-200 text-xs uppercase tracking-wide">Upcoming</p>
                  <p className="text-2xl font-bold">{scheduledCount}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3 border border-white/20">
                  <p className="text-indigo-200 text-xs uppercase tracking-wide">Completed</p>
                  <p className="text-2xl font-bold">{completedCount}</p>
                </div>
                {/* A2G Sync Button — Super Admin only */}
                {isSuperAdmin && (
                <button
                  onClick={handleSyncToA2G}
                  disabled={syncing}
                  className="flex items-center gap-2 px-4 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-2xl border border-white/30 transition-all text-white text-sm font-medium disabled:opacity-50"
                  title="Sync calendar to A2G Office"
                >
                  <RefreshCcw size={16} className={syncing ? 'animate-spin' : ''} />
                  <span>{syncing ? 'Syncing...' : 'Sync to A2G'}</span>
                </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* View Toggle & Navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-xl">
            <button
              onClick={() => setViewMode('month')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                viewMode === 'month' 
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <CalendarIcon size={18} />
              Month
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                viewMode === 'list' 
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <ListTodo size={18} />
              List
            </button>
          </div>

          {viewMode === 'month' && (
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={goToToday}
                className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
              >
                <Sparkles size={14} className="mr-1" />
                Today
              </Button>
              <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1">
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => navigateMonth('prev')}>
                  <ChevronLeft size={18} />
                </Button>
                <span className="px-4 font-semibold text-slate-900 min-w-[140px] text-center">
                  {format(currentDate, 'MMMM yyyy')}
                </span>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => navigateMonth('next')}>
                  <ChevronRight size={18} />
                </Button>
              </div>
            </div>
          )}
        </div>

        {viewMode === 'month' ? (
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Main Calendar */}
            <div className="xl:col-span-3">
              <Card className="border-0 shadow-xl shadow-slate-200/50 overflow-hidden">
                <CardContent className="p-0">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                      <div 
                        key={day} 
                        className={`py-4 text-center text-sm font-semibold ${
                          i === 0 || i === 6 ? 'text-slate-400' : 'text-slate-600'
                        }`}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7">
                    {calendarDays.map((day, index) => {
                      const dayMeetings = getMeetingsForDate(day);
                      const isSelected = selectedDate && isSameDay(day, selectedDate);
                      const isCurrentMonth = isSameMonth(day, currentDate);
                      const isTodayDate = isToday(day);
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                      
                      return (
                        <div
                          key={index}
                          onClick={() => setSelectedDate(day)}
                          className={`
                            min-h-[120px] p-2 border-b border-r border-slate-100 cursor-pointer
                            transition-all duration-200 hover:bg-indigo-50/50
                            ${!isCurrentMonth ? 'bg-slate-50/50' : isWeekend ? 'bg-slate-50/30' : 'bg-white'}
                            ${isSelected ? 'ring-2 ring-indigo-500 ring-inset bg-indigo-50' : ''}
                          `}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <span className={`
                              inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                              transition-all
                              ${isTodayDate 
                                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30' 
                                : isSelected 
                                  ? 'bg-indigo-100 text-indigo-700'
                                  : !isCurrentMonth 
                                    ? 'text-slate-300' 
                                    : 'text-slate-700 hover:bg-slate-100'
                              }
                            `}>
                              {format(day, 'd')}
                            </span>
                            {dayMeetings.length > 0 && (
                              <span className="text-xs font-medium text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">
                                {dayMeetings.length}
                              </span>
                            )}
                          </div>
                          
                          {/* Meeting Pills */}
                          <div className="space-y-1">
                            {dayMeetings.slice(0, 2).map((meeting) => (
                              <div
                                key={meeting.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenMeetingDetail(meeting);
                                }}
                                className={`
                                  group flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium
                                  truncate cursor-pointer transition-all
                                  ${meeting.status === 'scheduled' 
                                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                                    : meeting.status === 'completed'
                                      ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                                  }
                                `}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getStatusColor(meeting.status)}`} />
                                <span className="truncate">{meeting.title}</span>
                              </div>
                            ))}
                            {dayMeetings.length > 2 && (
                              <div className="text-xs text-slate-500 font-medium pl-2">
                                +{dayMeetings.length - 2} more
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Selected Date Details */}
              <Card className="border-0 shadow-xl shadow-slate-200/50 overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white pb-4">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <CalendarIcon size={18} />
                    {selectedDate ? format(selectedDate, 'EEE, MMM d') : 'Select a date'}
                  </CardTitle>
                  {selectedDate && isToday(selectedDate) && (
                    <Badge className="bg-white/20 text-white hover:bg-white/30 w-fit">Today</Badge>
                  )}
                </CardHeader>
                <CardContent className="p-4">
                  {selectedDateMeetings.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center">
                        <CalendarIcon size={24} className="text-slate-400" />
                      </div>
                      <p className="text-slate-500 font-medium">No meetings</p>
                      <p className="text-sm text-slate-400">This day is free</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedDateMeetings.map((meeting) => (
                        <div
                          key={meeting.id}
                          onClick={() => handleOpenMeetingDetail(meeting)}
                          className="group p-3 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all border border-transparent hover:border-slate-200"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
                              {meeting.title}
                            </h4>
                            {getStatusBadge(meeting.status)}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              {getTimeOfDayIcon(meeting.scheduled_at)}
                              {format(new Date(meeting.scheduled_at), 'h:mm a')}
                            </span>
                            {meeting.client_id && (
                              <span className="flex items-center gap-1">
                                <User size={14} />
                                {getClientName(meeting.client_id)}
                              </span>
                            )}
                          </div>
                          {meeting.meeting_url && (
                            <a
                              href={meeting.meeting_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="mt-2 flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                              <Video size={12} />
                              Join Meeting
                              <ExternalLink size={10} />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Upcoming Meetings */}
              <Card className="border-0 shadow-xl shadow-slate-200/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <Clock size={16} className="text-indigo-500" />
                    Upcoming
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {getUpcomingMeetings().length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No upcoming meetings</p>
                  ) : (
                    <div className="space-y-2">
                      {getUpcomingMeetings().map((meeting) => (
                        <div
                          key={meeting.id}
                          onClick={() => handleOpenMeetingDetail(meeting)}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                          <div className={`w-2 h-2 rounded-full ${getStatusColor(meeting.status)}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{meeting.title}</p>
                            <p className="text-xs text-slate-500">
                              {format(new Date(meeting.scheduled_at), 'MMM d, h:mm a')}
                            </p>
                          </div>
                          <ArrowRight size={14} className="text-slate-400" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* List View */
          <Card className="border-0 shadow-xl shadow-slate-200/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ListTodo size={20} className="text-indigo-500" />
                All Meetings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {meetings.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarIcon size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No meetings scheduled</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {meetings
                    .sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at))
                    .map((meeting) => (
                    <div
                      key={meeting.id}
                      onClick={() => handleOpenMeetingDetail(meeting)}
                      className="group flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all border border-transparent hover:border-slate-200"
                    >
                      <div className={`w-1 h-12 rounded-full ${getStatusColor(meeting.status)}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
                            {meeting.title}
                          </h4>
                          {getStatusBadge(meeting.status)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <CalendarIcon size={14} />
                            {format(new Date(meeting.scheduled_at), 'MMM d, yyyy')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {format(new Date(meeting.scheduled_at), 'h:mm a')}
                          </span>
                          {meeting.client_id && (
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              {getClientName(meeting.client_id)}
                            </span>
                          )}
                        </div>
                      </div>
                      {meeting.meeting_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(meeting.meeting_url, '_blank');
                          }}
                        >
                          <Video size={14} className="mr-1" />
                          Join
                        </Button>
                      )}
                      <ArrowRight size={18} className="text-slate-400 group-hover:text-indigo-500 transition-colors" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Meeting Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-slate-200 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="text-xl font-bold text-slate-900 mb-1">
                  {selectedMeeting?.title}
                </DialogTitle>
                <div className="flex items-center gap-3 text-sm text-slate-500">
                  {selectedMeeting && (
                    <>
                      <span className="flex items-center gap-1">
                        <CalendarIcon size={14} />
                        {format(new Date(selectedMeeting.scheduled_at), 'EEEE, MMMM d, yyyy')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {format(new Date(selectedMeeting.scheduled_at), 'h:mm a')}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {selectedMeeting && getStatusBadge(selectedMeeting.status)}
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-4">
            <div className="py-4 space-y-6">
              {/* Meeting Info */}
              <div className="grid grid-cols-2 gap-4">
                {selectedMeeting?.client_id && (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <User size={16} className="text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Client</p>
                      <p className="font-medium text-slate-900">{getClientName(selectedMeeting.client_id)}</p>
                    </div>
                  </div>
                )}
                {selectedMeeting?.organization_id && (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Building2 size={16} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Organization</p>
                      <p className="font-medium text-slate-900">{getTenantName(selectedMeeting.organization_id)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Meeting Link */}
              {selectedMeeting?.meeting_url && (
                <a
                  href={selectedMeeting.meeting_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Video size={20} />
                    <div>
                      <p className="font-semibold">Join Video Call</p>
                      <p className="text-indigo-200 text-sm">Click to open meeting room</p>
                    </div>
                  </div>
                  <ExternalLink size={18} />
                </a>
              )}

              {/* Description */}
              {selectedMeeting?.description && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Description</h4>
                  <p className="text-slate-600 bg-slate-50 p-3 rounded-xl">{selectedMeeting.description}</p>
                </div>
              )}

              {/* Tabs */}
              <Tabs defaultValue="comments" className="w-full">
                <TabsList className="w-full bg-slate-100">
                  <TabsTrigger value="comments" className="flex-1">
                    <MessageSquare size={14} className="mr-1" />
                    Comments ({comments.length})
                  </TabsTrigger>
                  <TabsTrigger value="reschedule" className="flex-1">
                    <RefreshCcw size={14} className="mr-1" />
                    Reschedule ({rescheduleRequests.length})
                  </TabsTrigger>
                  <TabsTrigger value="attachments" className="flex-1">
                    <Paperclip size={14} className="mr-1" />
                    Files ({attachments.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="comments" className="mt-4">
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {comments.length === 0 ? (
                      <p className="text-center text-slate-400 py-4">No comments yet</p>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment.id} className="p-3 bg-slate-50 rounded-xl">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-slate-900 text-sm">{comment.user_name}</span>
                            <span className="text-xs text-slate-400">
                              {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="text-slate-600 text-sm">{comment.content}</p>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Input
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                    />
                    <Button onClick={handleAddComment} disabled={commentLoading}>
                      {commentLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="reschedule" className="mt-4">
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {rescheduleRequests.length === 0 ? (
                      <p className="text-center text-slate-400 py-4">No reschedule requests</p>
                    ) : (
                      rescheduleRequests.map((request) => (
                        <div key={request.id} className="p-3 bg-slate-50 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-slate-900 text-sm">
                              Requested: {format(new Date(request.requested_date), 'MMM d, h:mm a')}
                            </span>
                            <Badge variant={
                              request.status === 'pending' ? 'secondary' :
                              request.status === 'approved' ? 'default' : 'destructive'
                            }>
                              {request.status}
                            </Badge>
                          </div>
                          {request.reason && <p className="text-slate-600 text-sm mb-2">{request.reason}</p>}
                          {request.status === 'pending' && isAdmin && (
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleRespondToReschedule(request.id, 'approve')}>
                                <CheckCircle size={14} className="mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleRespondToReschedule(request.id, 'reject')}>
                                <XCircle size={14} className="mr-1" /> Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  {isClient && selectedMeeting?.status === 'scheduled' && (
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => setIsRescheduleOpen(true)}
                    >
                      <RefreshCcw size={14} className="mr-2" />
                      Request Reschedule
                    </Button>
                  )}
                </TabsContent>

                <TabsContent value="attachments" className="mt-4">
                  {attachments.length === 0 ? (
                    <p className="text-center text-slate-400 py-4">No attachments</p>
                  ) : (
                    <div className="space-y-2">
                      {attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getFileIcon(attachment.content_type)}</span>
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{attachment.filename}</p>
                              <p className="text-xs text-slate-500">{formatFileSize(attachment.size)}</p>
                            </div>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => handleDownloadAttachment(attachment)}>
                            <Download size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </ScrollArea>

          {/* Actions Footer */}
          <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Select 
                  value={selectedMeeting?.status} 
                  onValueChange={handleUpdateMeetingStatus}
                >
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {meetingStatuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {isAdmin && !isClientTeam && (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => handleDeleteMeeting(selectedMeeting?.id)}
              >
                <Trash2 size={14} className="mr-1" />
                Delete
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Request Modal */}
      <Dialog open={isRescheduleOpen} onOpenChange={setIsRescheduleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCcw size={18} className="text-indigo-500" />
              Request Reschedule
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Preferred Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start mt-1">
                    <CalendarIcon size={16} className="mr-2" />
                    {rescheduleDate ? format(rescheduleDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={rescheduleDate}
                    onSelect={setRescheduleDate}
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>Preferred Time</Label>
              <Input
                type="time"
                value={rescheduleTime}
                onChange={(e) => setRescheduleTime(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                placeholder="Why do you need to reschedule?"
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                className="mt-1"
              />
            </div>
            <Button 
              className="w-full bg-indigo-600 hover:bg-indigo-700" 
              onClick={handleRequestReschedule}
              disabled={rescheduleLoading}
            >
              {rescheduleLoading ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Send size={16} className="mr-2" />
              )}
              Submit Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
